import type { LeadFormState } from '@core/Atoms/Pdp/FormState.atom';
import type { SearchParamsType } from '@core/Atoms/SearchParams/SearchParams.atom';
import type { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import type { AllExperiments } from '@growthBookExperimentation/Entities/Experiments.entities';
import type { FlagKey } from '@growthBookExperimentation/Entities/FlagKeys.entities';
import type { TrackingDataType } from '@tracking/Entities/TrackingDataType.entity';
import { leadStartToEvent } from '@tracking/Schemas/Lead/LeadStart/LeadStartToEvent';
import { vehicleToListingEvent } from '@tracking/Schemas/Listing/VehicleToListingEvent';
import { vehicleToListingFeatureEvent } from '@tracking/Schemas/ListingFeature/VehicleToListingFeatureEvent';
import { marketingToEvent } from '@tracking/Schemas/Marketing/MarketingToEvent';
import { userContextToEvent } from '@tracking/Schemas/UserContext/UserContextToEvent';
import { getExperimentViewedEvents } from '@tracking/Utils/GetExperimentViewedEvents';

interface Props {
  searchParams: SearchParamsType;
  userId: string | null | undefined;
  formState: LeadFormState;
  leadFormId: 'message-dealer' | 'leasing_quote' | 'finance_quote';
  listingId: string | null;
  vehicle: Vehicle;
  experiments?: AllExperiments;
}

export function generateLeadTrackingData({
  searchParams,
  userId,
  formState,
  leadFormId,
  listingId,
  vehicle,
  experiments,
}: Props): Record<string, TrackingDataType> {
  const marketingDataObj = {
    marketingValues: { ...(searchParams ?? {}) },
  };
  const userData = {
    userId,
    values: { ...formState },
  };
  const leadStartObj = {
    listingId,
    leadType: leadFormId as string,
    ctaLocation: 'finance_calculator',
  };

  // event actions
  const leadStartEvent = leadStartToEvent(leadStartObj);

  // event entities
  const userEvent = userContextToEvent(userData);
  const listingEvent = vehicleToListingEvent(vehicle);
  const marketingEvent = marketingToEvent(marketingDataObj);
  const listingFeatureEvent = vehicleToListingFeatureEvent(vehicle);

  const selectedLeadFormExperimentIds: FlagKey[] = [
    'message-field-leadform-experiment',
    'uk-enrichment-leadform-experiment',
    'name-field-leadform-experiment',
    'dealername-leadform-experiment',
  ];

  const selectedExperimentViewedEvents = getExperimentViewedEvents(
    selectedLeadFormExperimentIds,
    experiments,
  );

  const contextStart = [
    listingEvent,
    listingFeatureEvent,
    userEvent,
    marketingEvent,

    ...(leadFormId === 'message-dealer' && selectedExperimentViewedEvents.length > 0
      ? [...selectedExperimentViewedEvents]
      : []),
  ];

  return {
    leadStart: { event: leadStartEvent, context: contextStart },
  };
}
